import * as React from 'react';
import { cn } from '../../lib/cn';
import type { FlexAlign, PolymorphicComponentProps, SpacingScale } from '../../lib/types';

type StackGap = SpacingScale;

type StackAlign = FlexAlign;

type StackJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';

type StackDirection = 'column' | 'column-reverse' | 'row' | 'row-reverse';

const gapClasses = {
  '0': 'gap-0',
  '1': 'gap-1',
  '2': 'gap-2',
  '3': 'gap-3',
  '4': 'gap-4',
  '5': 'gap-5',
  '6': 'gap-6',
  '8': 'gap-8',
  '10': 'gap-10',
  '12': 'gap-12',
  '16': 'gap-16',
} satisfies Record<SpacingScale, string>;

const alignClasses = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
} satisfies Record<FlexAlign, string>;

const justifyClasses = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
} satisfies Record<StackJustify, string>;

const directionClasses = {
  column: 'flex-col',
  'column-reverse': 'flex-col-reverse',
  row: 'flex-row',
  'row-reverse': 'flex-row-reverse',
} satisfies Record<StackDirection, string>;

interface StackOwnProps {
  /** @default '4' */
  gap?: StackGap;
  /** Cross-axis alignment. @default 'stretch' */
  align?: StackAlign;
  /** Main-axis alignment. Default: none, so the CSS default applies. */
  justify?: StackJustify;
  /** @default 'column' */
  direction?: StackDirection;
  className?: string;
}

export type StackProps<T extends React.ElementType = 'div'> = PolymorphicComponentProps<
  T,
  StackOwnProps
>;

export function Stack<T extends React.ElementType = 'div'>({
  as,
  gap = '4',
  align = 'stretch',
  justify,
  direction = 'column',
  className,
  ref,
  ...props
}: StackProps<T>) {
  const Component = as ?? 'div';

  return (
    <Component
      ref={ref}
      className={cn(
        'flex',
        directionClasses[direction],
        gapClasses[gap],
        alignClasses[align],
        justify && justifyClasses[justify],
        className,
      )}
      {...props}
    />
  );
}
